<!-- NoteCreatorsStack.vue -->
<!-- Pilha vertical flutuante com os avatares dos criadores das notas visíveis  -->
<!-- no TextWithNotes, e popover que funciona como filtro por usuário.          -->
<!--                                                                            -->
<!-- PILHA e POPOVER saem da MESMA fonte: noteStore.allNotes, o universo NÃO     -->
<!-- filtrado da atividade. A pilha é um retrato de quem anotou a atividade e    -->
<!-- não encolhe com o filtro; `notes` (= noteStore.notesForHighlight) entra só  -->
<!-- para dizer QUEM está destacado agora — o criador com ao menos uma nota ali  -->
<!-- fica nítido, os demais esmaecidos. Como isso reage a notesForHighlight, e   -->
<!-- não à seleção de usuários, a nitidez acompanha TODOS os filtros (autoria,   -->
<!-- período, busca, tag), não apenas o filtro por usuário.                      -->
<!--                                                                            -->
<!-- Ambos incluem notas avulsas (range 0,0). Ordem do POPOVER: seguidos         -->
<!-- primeiro, depois recência — vale o createdAt da nota mais nova de cada um.  -->
<!-- A PILHA usa a mesma precedência com uma chave a mais na frente: filtrados   -->
<!-- primeiro, para que quem está nítido nunca caia no "+N". Não há contagem de  -->
<!-- notas em lugar nenhum. Seguidos ganham borda azul, na pilha e no popover.   -->
<!--                                                                            -->
<!-- O popover filtra por DUAS dimensões, em abas: usuários (a lista de sempre)  -->
<!-- e grupos (mesma fonte do SelectNoteGroup — noteStore.noteGroups). Elas não  -->
<!-- são exclusivas: são filtros independentes que o backend combina, então a    -->
<!-- aba troca a visão e as duas seleções seguem vivas, aplicadas juntas pelo    -->
<!-- "Filtrar" via applyCreatorSelectionFilter. O contador em cada aba existe    -->
<!-- para que a seleção fora de vista nunca filtre em silêncio.                  -->
<!--                                                                            -->
<!-- O filtro é o MESMO do SelectNoteUser: seleção temporária que só é aplicada  -->
<!-- no botão "Filtrar", via noteStore.applyUserSelectionFilter — a regra única  -->
<!-- compartilhada com o NoteFilter. A diferença está na SEMEADURA: quando o     -->
<!-- filtro por usuário não está explícito, as caixas nascem marcadas conforme   -->
<!-- quem está nítido na pilha, e não conforme selectedUserIdsForFilter (que aí  -->
<!-- está vazio). Como essa marcação não é escolha do usuário, o "Filtrar" só    -->
<!-- aplica se ela tiver sido alterada — ver o guard em applySelection.          -->

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import api from "@/services/api";
import { useNoteStore } from "@/stores/useNoteStore";
import { useUserStore } from "@/stores/useUserStore";

const props = defineProps({
  // Recorte filtrado (noteStore.notesForHighlight). Não define QUEM aparece na
  // pilha — define quem aparece nítido e promovido ao topo dela.
  notes: { type: Array, default: () => [] },
  // Elemento da área de leitura usado para ancorar o canto inferior direito.
  // Quando ausente, a pilha cai para o canto inferior direito da viewport.
  anchor: { type: Object, default: null },
});

const noteStore = useNoteStore();
const userStore = useUserStore();

const MAX_VISIBLE_AVATARS = 10;
const EDGE_GAP_PX = 20;

// ── Criadores ────────────────────────────────────────────────────────────────
// Ordem base: quem o usuário logado SEGUE vem primeiro; dentro de cada bloco,
// por recência. createdAt vem em ISO 8601, então a comparação lexicográfica
// equivale à cronológica — mesmo critério de mostFrequentNoteCreatorId no
// useNoteStore.
//
// `highlightedIds` (usado só pela pilha) acrescenta uma chave ANTES de todas:
// filtrados primeiro. É o que garante que um avatar nítido nunca fique atrás de
// um esmaecido — nem mesmo de um seguido com nota mais recente — e portanto
// nunca caia no "+N". Sem o argumento, a ordem é a original (popover).
function buildCreators(notes, highlightedIds = null) {
  const rawNotes = Array.isArray(notes) ? notes : [];
  const followedIds = userStore.followedUserIds;
  const byUser = new Map();

  for (const note of rawNotes) {
    const userId = note?.createdByUserId;
    if (userId === null || userId === undefined) continue;

    const createdAt = note?.createdAt ?? "";
    const photoPath =
      note?.createdByUserPhotoPath || note?.createdByUser?.photoPath || "";
    const existing = byUser.get(userId);

    if (!existing) {
      byUser.set(userId, {
        id: userId,
        name: note?.createdByUserName || "Usuário",
        photoPath,
        lastCreatedAt: createdAt,
        isFollowed: followedIds.has(userId),
        isFiltered: highlightedIds ? highlightedIds.has(userId) : false,
      });
      continue;
    }

    if (createdAt > existing.lastCreatedAt) existing.lastCreatedAt = createdAt;
    // Nem toda nota carrega foto/nome preenchidos; a primeira ocorrência não é
    // necessariamente a mais completa.
    if (!existing.photoPath && photoPath) existing.photoPath = photoPath;
    if (existing.name === "Usuário" && note?.createdByUserName) {
      existing.name = note.createdByUserName;
    }
  }

  return Array.from(byUser.values()).sort((a, b) => {
    if (highlightedIds && a.isFiltered !== b.isFiltered) {
      return a.isFiltered ? -1 : 1;
    }
    if (a.isFollowed !== b.isFollowed) return a.isFollowed ? -1 : 1;
    if (a.lastCreatedAt > b.lastCreatedAt) return -1;
    if (a.lastCreatedAt < b.lastCreatedAt) return 1;
    return a.name.localeCompare(b.name);
  });
}

// Criadores com ao menos uma nota no recorte filtrado. Sai de notesForHighlight
// (e não de selectedUserIdsForFilter) para que a nitidez reflita o destaque real
// no texto, seja qual for o filtro que o produziu.
const highlightedCreatorIds = computed(() => {
  const rawNotes = Array.isArray(props.notes) ? props.notes : [];
  const ids = new Set();

  for (const note of rawNotes) {
    const userId = note?.createdByUserId;
    if (userId === null || userId === undefined) continue;
    ids.add(userId);
  }

  return ids;
});

// Pilha: universo completo da atividade, com os filtrados marcados e promovidos.
const stackCreators = computed(() =>
  buildCreators(noteStore.allNotes, highlightedCreatorIds.value)
);

// Popover: mesmo universo, na ordem original (seguidos → recência). Reordenar a
// lista conforme o filtro faria as linhas pularem sob o cursor enquanto o
// usuário marca as caixas.
const filterCreators = computed(() => buildCreators(noteStore.allNotes));

const visibleCreators = computed(() =>
  stackCreators.value.slice(0, MAX_VISIBLE_AVATARS)
);

const overflowCount = computed(() =>
  Math.max(0, stackCreators.value.length - MAX_VISIBLE_AVATARS)
);

// Some pelo toggle do TextDisplayer ou por não haver criador na atividade. Um
// filtro que zera o destaque NÃO esconde mais a pilha: ela fica visível com
// todos os avatares esmaecidos, o que já diz "ninguém passando pelo filtro".
const isStackVisible = computed(
  () => noteStore.isCreatorsStackMode && stackCreators.value.length > 0
);

// Mesma normalização usada no resto do sistema (SelectNoteUser, NoteList…):
// URL externa (Google/Facebook) passa direto; caminho relativo ganha a base da
// API sem o sufixo "/api".
function resolveAvatarUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = (api.defaults.baseURL || "")
    .replace(/\/api$/, "")
    .replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

// ── Posicionamento ───────────────────────────────────────────────────────────
// A raiz do TextWithNotes é um <span> inline dentro de <p class="text-body">, e
// portanto não pode ancorar um filho posicionado. A pilha é teleportada para
// body e fixada a partir do retângulo da área de leitura.
const rootRef = ref(null);
const anchorStyle = ref({
  right: `${EDGE_GAP_PX}px`,
  bottom: `${EDGE_GAP_PX}px`,
});

let anchorResizeObserver = null;

function updatePosition() {
  const el = props.anchor;

  if (!el || typeof el.getBoundingClientRect !== "function") {
    anchorStyle.value = {
      right: `${EDGE_GAP_PX}px`,
      bottom: `${EDGE_GAP_PX}px`,
    };
    return;
  }

  const rect = el.getBoundingClientRect();

  anchorStyle.value = {
    right: `${Math.max(
      window.innerWidth - rect.right + EDGE_GAP_PX,
      EDGE_GAP_PX
    )}px`,
    bottom: `${Math.max(
      window.innerHeight - rect.bottom + EDGE_GAP_PX,
      EDGE_GAP_PX
    )}px`,
  };
}

function observeAnchor() {
  if (anchorResizeObserver) {
    anchorResizeObserver.disconnect();
    anchorResizeObserver = null;
  }

  const el = props.anchor;
  if (!el || typeof ResizeObserver !== "function") return;

  anchorResizeObserver = new ResizeObserver(updatePosition);
  anchorResizeObserver.observe(el);
}

watch(
  () => props.anchor,
  () => {
    observeAnchor();
    updatePosition();
  }
);

// ── Popover / filtro ─────────────────────────────────────────────────────────
const isPopoverOpen = ref(false);
const searchTerm = ref("");
const tempSelectedUserIds = ref([]);
const tempSelectedGroupIds = ref([]);

// Usuário e grupo são dimensões INDEPENDENTES no store (o NoteFilter as expõe
// como duas linhas que se combinam). A aba, portanto, troca a visão, não o
// filtro: a seleção da aba escondida continua viva e é aplicada junto — daí o
// contador aparecer nas duas abas, para que nada fique filtrando fora de vista.
const activeTab = ref("users");

const groups = computed(() => noteStore.noteGroups || []);
const hasGroups = computed(() => groups.value.length > 0);

// O que foi semeado na abertura, e se o filtro por usuário já estava explícito
// naquele momento. Os dois juntos alimentam o guard do applySelection.
const seededSelection = ref([]);
const seededGroupSelection = ref([]);
const hadExplicitUserFilterOnOpen = ref(false);

// Como no SelectNoteUser, a seleção é temporária e só volta para o store no
// botão "Filtrar". A semeadura, porém, tem DUAS origens — é o que faz o popover
// concordar com a pilha:
//  • filtro por usuário já explícito → espelha selectedUserIdsForFilter;
//  • filtro implícito (o padrão "Minhas notas", que vive em filterIncludeOwn com
//    selectedUserIdsForFilter vazio, ou qualquer outro recorte) → marca quem
//    está nítido na pilha.
// Sem o segundo caso, abrir uma atividade com notas próprias mostraria a lista
// inteira desmarcada enquanto o avatar do usuário logado aparece nítido — o
// mesmo widget se contradizendo.
// Grupo não tem semeadura implícita: nada na pilha representa grupo, então a
// aba nasce espelhando selectedGroupIdsForFilter — vazio fora do filtro
// explícito. Abrimos na aba que está filtrando, para o popover não esconder
// justamente a dimensão em uso.
watch(isPopoverOpen, (open) => {
  if (open) {
    hadExplicitUserFilterOnOpen.value = noteStore.filterIncludeSelectedUsers;
    tempSelectedUserIds.value = noteStore.filterIncludeSelectedUsers
      ? [...noteStore.selectedUserIdsForFilter]
      : [...highlightedCreatorIds.value];
    seededSelection.value = [...tempSelectedUserIds.value];

    tempSelectedGroupIds.value = [...noteStore.selectedGroupIdsForFilter];
    seededGroupSelection.value = [...tempSelectedGroupIds.value];

    activeTab.value =
      hasGroups.value &&
      noteStore.filterIncludeSelectedGroups &&
      !noteStore.filterIncludeSelectedUsers
        ? "groups"
        : "users";
  } else {
    searchTerm.value = "";
  }
});

// A busca vale para a lista que está à vista; o termo de uma aba não faz
// sentido na outra.
watch(activeTab, () => {
  searchTerm.value = "";
});

// Comparação por conjunto: a ordem de marcação não importa, e toggleUser mexe na
// lista por push/splice.
function isSameSelection(a, b) {
  if (a.length !== b.length) return false;
  const other = new Set(b);
  return a.every((id) => other.has(id));
}

const filteredCreators = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return filterCreators.value;
  return filterCreators.value.filter((creator) =>
    creator.name.toLowerCase().includes(term)
  );
});

// Blocos do popover: seguidos e demais, cada um já na ordem por recência
// herdada do buildCreators. Blocos vazios não entram na lista — é o que faz a
// linha divisória sumir sozinha quando um dos grupos não tem criador (seja
// porque o usuário não segue ninguém da atividade, seja porque a busca só
// devolveu gente de um lado).
const popoverSections = computed(() => {
  const followed = [];
  const others = [];

  for (const creator of filteredCreators.value) {
    if (creator.isFollowed) followed.push(creator);
    else others.push(creator);
  }

  const sections = [];
  if (followed.length > 0) {
    sections.push({ key: "followed", label: "Seguidos por mim", creators: followed });
  }
  if (others.length > 0) {
    sections.push({ key: "others", label: "Demais", creators: others });
  }
  return sections;
});

// "Todos" considera o universo inteiro, não o resultado da busca — igual ao
// SelectNoteUser.
const allSelected = computed({
  get: () =>
    filterCreators.value.length > 0 &&
    filterCreators.value.every((creator) =>
      tempSelectedUserIds.value.includes(creator.id)
    ),
  set: (value) => {
    tempSelectedUserIds.value = value
      ? filterCreators.value.map((creator) => creator.id)
      : [];
  },
});

function toggleAll() {
  allSelected.value = !allSelected.value;
}

function toggleUser(userId) {
  const index = tempSelectedUserIds.value.indexOf(userId);
  if (index >= 0) {
    tempSelectedUserIds.value.splice(index, 1);
  } else {
    tempSelectedUserIds.value.push(userId);
  }
}

// ── Grupos ───────────────────────────────────────────────────────────────────
// Mesma fonte do SelectNoteGroup: noteStore.noteGroups, já filtrado por
// atividade. Sem blocos "Seguidos/Demais" — a distinção não existe para grupo —
// e na ordem alfabética que o store entrega.
const filteredGroups = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return groups.value;
  return groups.value.filter((group) =>
    (group.name || "").toLowerCase().includes(term)
  );
});

const allGroupsSelected = computed({
  get: () =>
    groups.value.length > 0 &&
    groups.value.every((group) => tempSelectedGroupIds.value.includes(group.id)),
  set: (value) => {
    tempSelectedGroupIds.value = value
      ? groups.value.map((group) => group.id)
      : [];
  },
});

function toggleAllGroups() {
  allGroupsSelected.value = !allGroupsSelected.value;
}

function toggleGroup(groupId) {
  const index = tempSelectedGroupIds.value.indexOf(groupId);
  if (index >= 0) {
    tempSelectedGroupIds.value.splice(index, 1);
  } else {
    tempSelectedGroupIds.value.push(groupId);
  }
}

const hasAnySelection = computed(
  () =>
    tempSelectedUserIds.value.length > 0 || tempSelectedGroupIds.value.length > 0
);

// "Limpar" é local, como todo o resto do popover: desmarca as duas abas e
// espera o "Filtrar". Aplicado vazio, o applyCreatorSelectionFilter devolve a
// autoria ao padrão em vez de deixar a lista sem nenhum critério.
function clearSelection() {
  tempSelectedUserIds.value = [];
  tempSelectedGroupIds.value = [];
}

function togglePopover() {
  isPopoverOpen.value = !isPopoverOpen.value;
  if (isPopoverOpen.value) nextTick(updatePosition);
}

function closePopover() {
  isPopoverOpen.value = false;
}

async function applySelection() {
  // Guard: a seleção semeada a partir de um filtro IMPLÍCITO não é escolha do
  // usuário. Aplicá-la converteria "Minhas notas" em "notas do usuário nº N" —
  // applyUserSelectionFilter liga filterIncludeSelectedUsers e zera
  // filterIncludeOwn/filterIncludeComments. Não muda nada na tela, mas deixa um
  // estado onde desmarcar-se depois derruba as duas flags de autoria e esconde
  // TODAS as notas. Sem tocar nas caixas, "Filtrar" apenas fecha.
  // Com duas dimensões, o "não mexeu em nada" só vale se NENHUMA das duas
  // mudou. A de grupo sai de estado explícito, então qualquer alteração ali é
  // intenção do usuário e derruba o guard.
  if (
    !hadExplicitUserFilterOnOpen.value &&
    isSameSelection(tempSelectedUserIds.value, seededSelection.value) &&
    isSameSelection(tempSelectedGroupIds.value, seededGroupSelection.value)
  ) {
    closePopover();
    return;
  }

  // Mesma sequência do applyUserFilter em NoteFilter.vue: a regra de estado sai
  // da action compartilhada, e o refetch da lista paginada vem do setNoteFilters.
  noteStore.applyCreatorSelectionFilter({
    userIds: tempSelectedUserIds.value,
    groupIds: tempSelectedGroupIds.value,
  });
  closePopover();

  await noteStore.setNoteFilters({
    includeOwn: noteStore.filterIncludeOwn,
    comments: noteStore.filterIncludeComments,
    sortBy: noteStore.filterSortOrder,
    includeSelectedUsers: noteStore.filterIncludeSelectedUsers,
    includeSelectedGroups: noteStore.filterIncludeSelectedGroups,
  });
}

// Sem isso o popover ficaria marcado como aberto após a pilha sumir, e
// reapareceria já aberto ao voltar.
watch(isStackVisible, (visible) => {
  if (!visible) closePopover();
});

function handleGlobalPointerDown(event) {
  if (!isPopoverOpen.value) return;
  if (rootRef.value && rootRef.value.contains(event.target)) return;
  closePopover();
}

function handleGlobalKeydown(event) {
  if (event.key === "Escape") closePopover();
}

onMounted(() => {
  // Sem await: a pilha renderiza por recência e reordena sozinha quando o Set
  // chega. Não bloqueia a leitura, e falhando fica só a recência.
  userStore.fetchFollowedUserIds();

  observeAnchor();
  updatePosition();

  // capture: a área de leitura rola num container interno, não na window.
  document.addEventListener("scroll", updatePosition, true);
  window.addEventListener("resize", updatePosition);
  document.addEventListener("mousedown", handleGlobalPointerDown, true);
  document.addEventListener("keydown", handleGlobalKeydown);
});

onUnmounted(() => {
  document.removeEventListener("scroll", updatePosition, true);
  window.removeEventListener("resize", updatePosition);
  document.removeEventListener("mousedown", handleGlobalPointerDown, true);
  document.removeEventListener("keydown", handleGlobalKeydown);

  if (anchorResizeObserver) {
    anchorResizeObserver.disconnect();
    anchorResizeObserver = null;
  }
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isStackVisible"
      ref="rootRef"
      class="ncs-root"
      :style="anchorStyle"
    >
      <!-- ── Popover: filtro por usuário ─────────────────────────────────── -->
      <transition name="ncs-fade">
        <div
          v-if="isPopoverOpen"
          class="ncs-popover"
          role="dialog"
          aria-label="Filtrar por criador das notas"
        >
          <div class="ncs-popover-header">Criadores das notas</div>

          <div class="ncs-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              class="ncs-tab"
              :class="{ 'ncs-tab--active': activeTab === 'users' }"
              :aria-selected="activeTab === 'users'"
              @click="activeTab = 'users'"
            >
              Usuários
              <span v-if="tempSelectedUserIds.length" class="ncs-tab-badge">
                {{ tempSelectedUserIds.length }}
              </span>
            </button>

            <button
              type="button"
              role="tab"
              class="ncs-tab"
              :class="{ 'ncs-tab--active': activeTab === 'groups' }"
              :aria-selected="activeTab === 'groups'"
              :disabled="!hasGroups"
              :title="
                hasGroups
                  ? null
                  : 'Nenhum grupo com notas nesta atividade'
              "
              @click="activeTab = 'groups'"
            >
              Grupos
              <span v-if="tempSelectedGroupIds.length" class="ncs-tab-badge">
                {{ tempSelectedGroupIds.length }}
              </span>
            </button>
          </div>

          <div class="ncs-search-section">
            <label for="ncs-search" class="ncs-sr-only">Buscar</label>
            <input
              id="ncs-search"
              v-model="searchTerm"
              type="text"
              class="ncs-search-input"
              :placeholder="
                activeTab === 'users' ? 'Buscar usuários' : 'Buscar grupos'
              "
              autocomplete="off"
              @keydown.enter.prevent="applySelection"
            />
          </div>

          <ul v-if="activeTab === 'users'" class="ncs-popover-list">
            <li
              v-if="filterCreators.length > 0"
              class="ncs-popover-row"
              @click="toggleAll"
            >
              <span class="ncs-popover-name ncs-popover-name--all">
                Todos os usuários
              </span>
              <input
                v-model="allSelected"
                type="checkbox"
                class="ncs-checkbox"
                aria-label="Selecionar todos os usuários"
                @click.stop
              />
            </li>

            <!-- Rótulos só quando há os DOIS blocos: com um só, não há o que
                 distinguir e o rótulo vira ruído. -->
            <template v-for="section in popoverSections" :key="section.key">
              <li v-if="popoverSections.length > 1" class="ncs-section-label">
                {{ section.label }}
              </li>

              <!-- Toda a linha (inclusive avatar + nome) marca/desmarca. -->
              <li
                v-for="creator in section.creators"
                :key="creator.id"
                class="ncs-popover-row"
                @click="toggleUser(creator.id)"
              >
                <span class="ncs-popover-identity">
                  <img
                    v-if="resolveAvatarUrl(creator.photoPath)"
                    class="ncs-popover-avatar"
                    :class="{
 'ncs-popover-avatar--followed': creator.isFollowed,
                    }"
                    :src="resolveAvatarUrl(creator.photoPath)"
                    :alt="creator.name"
                  />
                  <span
                    v-else
                    class="ncs-popover-avatar ncs-avatar-fallback"
                    :class="{
 'ncs-popover-avatar--followed': creator.isFollowed,
                    }"
                  >
                    <i class="fas fa-user"></i>
                  </span>

                  <span class="ncs-popover-name" :title="creator.name">{{
                    creator.name
                  }}</span>
                </span>

                <input
                  v-model="tempSelectedUserIds"
                  :value="creator.id"
                  type="checkbox"
                  class="ncs-checkbox"
                  :aria-label="`Filtrar por ${creator.name}`"
                  @click.stop
                />
              </li>
            </template>

            <li v-if="popoverSections.length === 0" class="ncs-empty-item">
              Nenhum usuário encontrado.
            </li>
          </ul>

          <ul v-else class="ncs-popover-list">
            <li
              v-if="groups.length > 0"
              class="ncs-popover-row"
              @click="toggleAllGroups"
            >
              <span class="ncs-popover-name ncs-popover-name--all">
                Todos os grupos
              </span>
              <input
                v-model="allGroupsSelected"
                type="checkbox"
                class="ncs-checkbox"
                aria-label="Selecionar todos os grupos"
                @click.stop
              />
            </li>

            <li
              v-for="group in filteredGroups"
              :key="group.id"
              class="ncs-popover-row"
              @click="toggleGroup(group.id)"
            >
              <span class="ncs-popover-identity">
                <span class="ncs-popover-avatar ncs-avatar-fallback">
                  <i class="fas fa-users"></i>
                </span>
                <span class="ncs-popover-name" :title="group.name">{{
                  group.name
                }}</span>
              </span>

              <input
                v-model="tempSelectedGroupIds"
                :value="group.id"
                type="checkbox"
                class="ncs-checkbox"
                :aria-label="`Filtrar por ${group.name}`"
                @click.stop
              />
            </li>

            <li v-if="filteredGroups.length === 0" class="ncs-empty-item">
              Nenhum grupo encontrado.
            </li>
          </ul>

          <div class="ncs-popover-actions">
            <button
              v-if="hasAnySelection"
              type="button"
              class="ncs-btn ncs-btn-secondary"
              @click="clearSelection"
            >
              Limpar
            </button>
            <button
              type="button"
              class="ncs-btn ncs-btn-primary"
              @click="applySelection"
            >
              Filtrar
            </button>
          </div>
        </div>
      </transition>

      <!-- ── Pilha de avatares ───────────────────────────────────────────── -->
      <button
        type="button"
        class="ncs-stack"
        :aria-expanded="isPopoverOpen"
        aria-label="Filtrar por criador das notas"
        title="Filtrar por criador das notas"
        @click.stop="togglePopover"
      >
        <span
          v-for="creator in visibleCreators"
          :key="creator.id"
          class="ncs-avatar"
          :class="{
 'ncs-avatar--followed': creator.isFollowed,
            'ncs-avatar--dimmed': !creator.isFiltered,
          }"
        >
          <img
            v-if="resolveAvatarUrl(creator.photoPath)"
            :src="resolveAvatarUrl(creator.photoPath)"
            :alt="creator.name"
          />
          <span v-else class="ncs-avatar-fallback">
            <i class="fas fa-user"></i>
          </span>
        </span>

        <span v-if="overflowCount > 0" class="ncs-avatar ncs-avatar--more">
          +{{ overflowCount }}
        </span>
      </button>
    </div>
  </Teleport>
</template>

<style scoped>
.ncs-root {
  position: fixed;
  z-index: 500;
  display: flex;
  align-items: flex-end;
  user-select: none;
}

.ncs-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Pilha ──────────────────────────────────────────────────────────────── */
/* A pilha inteira fica em opacidade cheia: o esmaecimento desceu para o avatar,
   onde distingue quem passa pelo filtro de quem não passa. Por isso o hover não
   altera mais a aparência — a nitidez virou informação, não afordância. */
.ncs-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.ncs-stack:focus {
  outline: none;
}

.ncs-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  flex: 0 0 auto;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  background-color: var(--neutral-200);
  transition: opacity 0.18s ease-in-out;
}

/* Criador sem nenhuma nota no recorte filtrado: presente, porém apagado. */
.ncs-avatar--dimmed {
  opacity: 0.62;
}

/* Criadores que o usuário logado segue: borda azul no lugar da branca. */
.ncs-avatar--followed {
  border-color: var(--link);
}

.ncs-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ncs-avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--neutral-200);
  color: var(--text-light);
  font-size: 0.75rem;
}

.ncs-avatar--more {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--brand-light);
  color: var(--text-dark);
  font-size: 0.7rem;
  font-weight: 600;
}

/* ── Popover ────────────────────────────────────────────────────────────── */
.ncs-popover {
  position: absolute;
  right: calc(100% + 10px);
  bottom: 0;
  width: 232px;
  background-color: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.ncs-popover-header {
  padding: 8px 10px 6px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #888;
}

/* ── Abas ───────────────────────────────────────────────────────────────── */
/* Duas metades de largura igual, na mesma linguagem dos toggles do leitor:
   ativa em lilás sólido, inativa apenas texto. */
.ncs-tabs {
  display: flex;
  gap: 4px;
  padding: 0 10px 8px;
}

.ncs-tab {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 26px;
  padding: 0 6px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: none;
  color: #888;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.ncs-tab:hover:not(:disabled):not(.ncs-tab--active) {
  background-color: var(--surface-hover);
}

.ncs-tab--active {
  background-color: var(--brand-light);
  border-color: transparent;
  color: var(--text-dark);
}

.ncs-tab:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ncs-tab:focus,
.ncs-tab:focus-visible {
  outline: none;
}

/* Contador da dimensão — aparece também na aba inativa, para que uma seleção
   fora de vista nunca filtre em silêncio. */
.ncs-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  border-radius: 999px;
  background-color: var(--surface);
  color: #6d5ce7;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
}

.ncs-tab:not(.ncs-tab--active) .ncs-tab-badge {
  background-color: #ede9ff;
}

.ncs-search-section {
  padding: 0 10px 8px;
}

.ncs-search-input {
  background-color: #f7f7f7;
  border: 1px solid var(--border-color);
  color: var(--text-dark);
  font-size: 0.82rem;
  border-radius: 8px;
  display: block;
  width: 100%;
  padding: 5px 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  outline: none;
}

/* ~8 linhas antes de rolar. */
.ncs-popover-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 272px;
  overflow-y: auto;
}

.ncs-popover-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.ncs-popover-row:hover {
  background-color: var(--surface-hover);
}

.ncs-popover-identity {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.ncs-popover-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
  overflow: hidden;
  /* box-sizing explícito: a borda dos seguidos entra para dentro, mantendo
     todas as linhas da lista alinhadas. */
  box-sizing: border-box;
}

/* Mesma borda azul da pilha. */
.ncs-popover-avatar--followed {
  border: 2px solid var(--link);
}

.ncs-popover-name {
  font-size: 0.82rem;
  color: var(--text-dark);
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncs-popover-name--all {
  font-weight: 600;
}

/* Rótulo do bloco: sem linha, alinhado à esquerda — mesma linguagem do
   cabeçalho do popover. */
.ncs-section-label {
  padding: 7px 10px 3px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #888;
  white-space: nowrap;
}

.ncs-empty-item {
  padding: 8px 10px;
  text-align: center;
  color: #999;
  font-style: italic;
  font-size: 0.8rem;
}

/* Checkbox — mesma linguagem visual do SelectNoteUser.vue */
.ncs-checkbox {
  width: 0.95rem;
  height: 0.95rem;
  border: 1px solid var(--text-subtle);
  border-radius: 3px;
  background-color: var(--surface);
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  flex-shrink: 0;
  margin: 0;
  transition: all 0.15s ease-in-out;
}

.ncs-checkbox:hover {
  border-color: var(--brand-light);
}

.ncs-checkbox:checked {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
}

.ncs-checkbox:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Ações */
.ncs-popover-actions {
  padding: 8px 10px;
  border-top: 1px solid var(--border-color);
  margin: 6px 10px 0 10px;
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
}

.ncs-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.ncs-btn-primary {
  background-color: var(--brand-light);
  color: var(--text-dark);
}

.ncs-btn-primary:hover {
  background-color: var(--brand-light-hover);
}

.ncs-btn-primary:active {
  transform: translateY(1px);
}

.ncs-btn-primary:focus,
.ncs-btn-primary:focus-visible {
  outline: none;
  box-shadow: none;
}

/* Mesma dupla primário/secundário do SelectNoteGroup. */
.ncs-btn-secondary {
  background-color: var(--neutral-200);
  color: var(--text-dark);
}

.ncs-btn-secondary:hover {
  background-color: var(--neutral-400);
}

.ncs-btn-secondary:active {
  transform: translateY(1px);
}

.ncs-btn-secondary:focus,
.ncs-btn-secondary:focus-visible {
  outline: none;
  box-shadow: none;
}

/* ── Transição ──────────────────────────────────────────────────────────── */
.ncs-fade-enter-active,
.ncs-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.ncs-fade-enter-from,
.ncs-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
